import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import MinimalHeader, { goToContact } from "@/components/MinimalHeader";
import MinimalFooter from "@/components/MinimalFooter";

const projects = [
  {
    title: "Atelier Margaux",
    category: "Artisanat — céramique",
    description:
      "Boutique en ligne sur mesure pour une céramiste normande : catalogue produit, prises de commande personnalisées et prise de RDV atelier.",
    tags: ["E-commerce", "Sur mesure", "SEO local"],
    image:
      "https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=1200&h=800&fit=crop&q=80",
  },
  {
    title: "Cabinet Lenoir & Associés",
    category: "Services — conseil juridique",
    description:
      "Site institutionnel pour un cabinet d'avocats : présentation des équipes, expertise sectorielle et prise de rendez-vous intégrée.",
    tags: ["Vitrine", "Prise de RDV", "Blog"],
    image:
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=800&fit=crop&q=80",
  },
  {
    title: "La Table d'Henri",
    category: "Restauration",
    description:
      "Site de restaurant avec menu dynamique, réservation en ligne, galerie et intégration Google Business pour le référencement local.",
    tags: ["Réservation", "SEO local", "Responsive"],
    image:
      "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1200&h=800&fit=crop&q=80",
  },
  {
    title: "Nova Conseil",
    category: "B2B — conseil stratégique",
    description:
      "Plateforme de génération de leads pour une société de conseil : landing pages par secteur, formulaires qualifiés et intégration CRM.",
    tags: ["Lead gen", "Landing pages", "CRM"],
    image:
      "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&h=800&fit=crop&q=80",
  },
  {
    title: "Studio Clémence",
    category: "Photographie de mariage",
    description:
      "Portfolio immersif avec galerie plein écran, témoignages clients et formulaire de devis personnalisé par type de prestation.",
    tags: ["Portfolio", "Devis", "Images optimisées"],
    image:
      "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&h=800&fit=crop&q=80",
  },
  {
    title: "Marché Saint-Lô",
    category: "Association — commerce local",
    description:
      "Site d'une association de commerçants : annuaire des membres, actualités, événements et carte interactive du centre-ville.",
    tags: ["Annuaire", "Carte", "Événements"],
    image:
      "https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=1200&h=800&fit=crop&q=80",
  },
];

const Hero = () => (
  <section className="pt-32 pb-16 px-6">
    <div className="max-w-3xl mx-auto text-center">
      <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-6">
        Nos réalisations
      </p>
      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
        Des sites <span className="text-primary">pensés</span> pour chaque métier.
      </h1>
      <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
        Un aperçu des projets que nous concevons pour des entrepreneurs, artisans
        et PME. Chaque site est unique — aucun template, aucun copié-collé.
      </p>
    </div>
  </section>
);

const ProjectsGrid = () => (
  <section className="pb-20 px-6">
    <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
      {projects.map((project) => (
        <article
          key={project.title}
          className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-colors"
        >
          <div className="aspect-[16/10] overflow-hidden bg-muted">
            <img
              src={project.image}
              alt={project.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">
              {project.category}
            </p>
            <h3 className="text-xl font-semibold mb-3 leading-tight">{project.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              {project.description}
            </p>
            <div className="flex flex-wrap gap-2">
              {project.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-muted text-xs font-medium text-muted-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>
      ))}
    </div>
  </section>
);

const CtaBlock = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <section className="py-20 px-6 border-t border-border/50">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-5">
          Votre projet pourrait être le prochain.
        </h2>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8">
          Parlez-nous de votre activité, on revient vers vous sous 48h avec un devis gratuit.
        </p>
        <Button
          onClick={() => goToContact(navigate, location)}
          size="lg"
          className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8 text-base font-semibold shadow-lg shadow-primary/15 group"
        >
          Démarrer mon projet
          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </section>
  );
};

const Realisations = () => (
  <div className="min-h-screen bg-background text-foreground">
    <SEO
      title="Nos réalisations — Sites web sur mesure"
      description="Découvrez les sites internet sur mesure réalisés par Lacrea pour des entrepreneurs, artisans et PME. E-commerce, vitrine, portfolio, B2B."
      path="/realisations"
    />
    <MinimalHeader />
    <main>
      <Hero />
      <ProjectsGrid />
      <CtaBlock />
    </main>
    <MinimalFooter />
  </div>
);

export default Realisations;
